import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, json } from "../../../api";
import {
  getEffectiveRepositoryIds,
  hasRepositorySelectionChanged,
} from "../helpers/get-effective-repository-ids";
import type { GitHubConnection } from "../types/github";

export function useGitHub(workspaceId: string | undefined) {
  const query = useQuery({
    queryKey: ["github", workspaceId],
    queryFn: () => api<GitHubConnection>(`/${workspaceId}/github`),
  });
  const [selectedIds, setSelectedIds] = useState<number[] | null>(null);
  const repositories = query.data?.repositories ?? [];
  const effectiveIds = getEffectiveRepositoryIds(repositories, selectedIds);
  const hasSelectionChanges = hasRepositorySelectionChanged(
    repositories,
    selectedIds,
  );

  const sync = useMutation({
    mutationFn: () => api(`/${workspaceId}/github/sync`, json({})),
    onSuccess: () => query.refetch(),
  });

  const saveSelection = useMutation({
    mutationFn: () =>
      api(`/${workspaceId}/github/repositories`, {
        method: "PUT",
        body: JSON.stringify({ repositoryIds: effectiveIds }),
      }),
    onSuccess: async () => {
      await query.refetch();
      setSelectedIds(null);
    },
  });

  const disconnect = useMutation({
    mutationFn: () => api(`/${workspaceId}/github/disconnect`, json({})),
    onSuccess: () => query.refetch(),
  });

  function toggleRepository(repositoryId: number, selected: boolean) {
    saveSelection.reset();
    setSelectedIds(
      selected
        ? [...effectiveIds, repositoryId]
        : effectiveIds.filter((id) => id !== repositoryId),
    );
  }

  return {
    query,
    repositories,
    effectiveIds,
    hasSelectionChanges,
    sync,
    saveSelection,
    disconnect,
    toggleRepository,
  };
}
